// FAQ.jsx — главная страница F.A.Q.
// Шапка + PageIntro + поиск с подсказками + сетка категорий с иконками.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminService from '../../../API/AdminService';
import cl from './FAQ.module.css';
import logger from '../../../utils/logger';
import PageIntro from '../../../components/PageIntro/PageIntro';
import {
    IconHelp, IconSearch, IconClose, IconRefresh, IconArrowRight,
    IconGamepad, IconChart, IconShieldCheck, IconBook, IconAlert,
} from '../../../components/Icons';

// Маппинг внутренних имён категорий FAQ → иконка + цветовой тон rail-бара.
const CATEGORY_VIEW = {
    integration:  { icon: <IconArrowRight width={22} height={22}/>, tone: 'blue' },
    programming:  { icon: <IconChart width={22} height={22}/>,      tone: 'green' },
    robotics:     { icon: <IconShieldCheck width={22} height={22}/>,tone: 'red' },
    admin_panel:  { icon: <IconBook width={22} height={22}/>,        tone: 'gold' },
    minecraftia:  { icon: <IconGamepad width={22} height={22}/>,    tone: 'violet' },
};
function getCategoryView(name) {
    return CATEGORY_VIEW[name] || { icon: <IconBook width={22} height={22}/>, tone: 'gray' };
}

const FAQ = () => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            setLoading(true);
            const response = await AdminService.getFAQCategories();
            setCategories(response.data || []);
            setError('');
        } catch (err) {
            logger.error('Ошибка загрузки категорий FAQ:', err);
            setError('Не удалось загрузить FAQ');
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = async (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        if (query.length >= 2) {
            try {
                const response = await AdminService.getFAQSearchSuggestions(query);
                setSearchSuggestions(response.data || []);
                setShowSuggestions(true);
            } catch (err) {
                logger.error('Ошибка получения подсказок:', err);
                setSearchSuggestions([]);
            }
        } else {
            setSearchSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/main/faq/search?q=${encodeURIComponent(searchQuery.trim())}`);
        }
        setShowSuggestions(false);
    };

    const handleSuggestionClick = (suggestion) => {
        navigate(`/main/faq/articles/${suggestion.id}`);
        setShowSuggestions(false);
        setSearchQuery('');
    };

    const clearSearch = () => {
        setSearchQuery('');
        setSearchSuggestions([]);
        setShowSuggestions(false);
    };

    if (loading) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.loadingContainer}>
                        <div className={cl.spinner}/>
                        <p>Загрузка F.A.Q.…</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.errorState}>
                        <span className={cl.errorIcon}>
                            <IconAlert width={20} height={20}/>
                        </span>
                        <h3>Не удалось загрузить F.A.Q.</h3>
                        <p>{error}</p>
                        <button onClick={loadCategories} className={cl.retryButton}>
                            <IconRefresh width={14} height={14}/>
                            <span>Попробовать снова</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const totalArticles = categories.reduce(
        (sum, c) => sum + (c.articles_count || 0),
        0,
    );

    return (
        <div className={cl.page_wrapper}>
            <div className={cl.wrap_content_1}>
                {/* Шапка */}
                <div className={cl.pageHeaderV2}>
                    <div className={cl.pageHeaderLeft}>
                        <div className={cl.pageHeaderIcon}>
                            <IconHelp width={22} height={22}/>
                        </div>
                        <div className={cl.pageHeaderText}>
                            <div className={cl.pageHeaderTopRow}>
                                <h1 className={cl.pageHeaderTitle}>F.A.Q.</h1>
                                {totalArticles > 0 && (
                                    <span className={cl.pageHeaderPill}>
                                        <span className={cl.pageHeaderPillStrong}>{totalArticles}</span>
                                        <span className={cl.pageHeaderPillMuted}>
                                            {pluralRu(totalArticles, 'статья', 'статьи', 'статей')}
                                        </span>
                                    </span>
                                )}
                                {categories.length > 0 && (
                                    <span className={cl.pageHeaderPill}>
                                        <span className={cl.pageHeaderPillStrong}>{categories.length}</span>
                                        <span className={cl.pageHeaderPillMuted}>
                                            {pluralRu(categories.length, 'раздел', 'раздела', 'разделов')}
                                        </span>
                                    </span>
                                )}
                            </div>
                            <div className={cl.breadcrumbs}>
                                АдминПанель · Материалы · F.A.Q.
                            </div>
                        </div>
                    </div>
                    <div className={cl.pageHeaderActions}>
                        <button
                            onClick={loadCategories}
                            className={cl.btnGhostV2}
                            title="Обновить"
                        >
                            <IconRefresh width={15} height={15}/>
                        </button>
                    </div>
                </div>

                <PageIntro
                    storageKey="faq"
                    title="О странице «F.A.Q.»"
                    text="Полное руководство по работе с системой: как вести занятия по робототехнике и программированию, как настраивать админ-панель, как методика обучения связана с Майнкрафтией. Если коротко — это база знаний для преподавателей и руководителей центров."
                    bullets={[
                        'Поиск ищет по всем статьям сразу — введите 2 буквы и появятся подсказки',
                        'Клик по разделу открывает список его статей',
                        'Если не нашли ответ — задайте вопрос на форуме',
                    ]}
                />

                {/* Поиск с подсказками */}
                <div className={cl.searchCard}>
                    <form onSubmit={handleSearchSubmit} className={cl.searchForm}>
                        <span className={cl.searchInputIcon}>
                            <IconSearch width={18} height={18}/>
                        </span>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            placeholder="Поиск по всем статьям F.A.Q.…"
                            className={cl.searchInput}
                            onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)}
                            onBlur={() => setTimeout(() => setShowSuggestions(false), 180)}
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={clearSearch}
                                className={cl.searchClearButton}
                                aria-label="Очистить"
                            >
                                <IconClose width={13} height={13}/>
                            </button>
                        )}
                        <button
                            type="submit"
                            className={cl.searchSubmitButton}
                            disabled={!searchQuery.trim()}
                        >
                            <IconSearch width={14} height={14}/>
                            <span className={cl.searchSubmitText}>Найти</span>
                        </button>

                        {showSuggestions && searchSuggestions.length > 0 && (
                            <div className={cl.suggestions}>
                                {searchSuggestions.map(suggestion => (
                                    <button
                                        key={suggestion.id}
                                        type="button"
                                        className={cl.suggestionItem}
                                        onMouseDown={() => handleSuggestionClick(suggestion)}
                                    >
                                        <span className={cl.suggestionTitle}>{suggestion.title}</span>
                                        <span className={cl.suggestionCategory}>
                                            {categories.find(cat => cat.name === suggestion.category_name)?.title}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </form>
                </div>

                {/* Категории */}
                <div className={cl.sectionTitle}>Разделы F.A.Q.</div>
                {categories.length === 0 ? (
                    <div className={cl.emptyCard}>Разделы пока не созданы</div>
                ) : (
                    <div className={cl.categoriesGrid}>
                        {categories.map(category => (
                            <CategoryCard
                                key={category.id}
                                category={category}
                                onClick={() => navigate(`/main/faq/category/${category.name}`)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};


// ----- Карточка категории -----
const CategoryCard = ({ category, onClick }) => {
    const view = getCategoryView(category.name);
    const count = category.articles_count || 0;
    return (
        <button
            type="button"
            className={`${cl.categoryCard} ${cl[`categoryCard_${view.tone}`]}`}
            onClick={onClick}
        >
            <div className={cl.categoryCardIcon}>{view.icon}</div>
            <div className={cl.categoryCardBody}>
                <div className={cl.categoryCardHead}>
                    <h3 className={cl.categoryTitle}>{category.title}</h3>
                    <IconArrowRight width={14} height={14} className={cl.categoryArrow}/>
                </div>
                {category.description && (
                    <p className={cl.categoryDescription}>{category.description}</p>
                )}
                <div className={cl.categoryFooter}>
                    <span className={cl.categoryCountPill}>
                        <IconBook width={11} height={11}/>
                        {count} {pluralRu(count, 'статья', 'статьи', 'статей')}
                    </span>
                </div>
            </div>
        </button>
    );
};

function pluralRu(n, one, few, many) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
    return many;
}

export default FAQ;
